"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import ConfirmLeaveDialog from "@/components/ux/ConfirmLeaveDialog";
import { useLoading } from "@/app/context/LoadingProvider";

type NavigationGuardContextType = {
  setGuardActive: (active: boolean) => void;
  guardedPush: (href: string) => void;
};

const NavigationGuardContext = createContext<NavigationGuardContextType | undefined>(undefined);

export const useNavigationGuard = () => {
  const ctx = useContext(NavigationGuardContext);
  if (!ctx) throw new Error("useNavigationGuard must be used within NavigationGuardProvider");
  return ctx;
};

export function NavigationGuardProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { setPageLoading } = useLoading();
  // A ref (not state) so the document-level click listener below always reads
  // the latest value without needing to re-attach the listener on every change.
  const guardActiveRef = useRef(false);
  const [pendingHref, setPendingHref] = useState<string | null>(null);

  const setGuardActive = useCallback((active: boolean) => {
    guardActiveRef.current = active;
  }, []);

  // Only flips the loading overlay on right when navigation actually happens
  // (here, or in handleConfirm below) — never just for showing the confirm
  // dialog, or Cancel would leave the overlay stuck on with nothing left to
  // reset it (the app only clears it on a pathname change).
  const guardedPush = useCallback(
    (href: string) => {
      if (guardActiveRef.current) {
        setPendingHref(href);
      } else {
        setPageLoading(true);
        router.push(href);
      }
    },
    [router, setPageLoading],
  );

  // Capture-phase so this runs before Next's <Link> navigation (and any
  // onClick already on the link, e.g. setPageLoading(true)) ever fires —
  // stopping propagation here means those simply never run. When the guard
  // is off this bails out on the first line, so normal navigation elsewhere
  // in the app is completely unaffected.
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!guardActiveRef.current) return;
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.target && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;

      event.preventDefault();
      event.stopPropagation();
      setPendingHref(url.pathname + url.search + url.hash);
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  const handleConfirm = () => {
    const href = pendingHref;
    setGuardActive(false);
    setPendingHref(null);
    if (href) {
      setPageLoading(true);
      router.push(href);
    }
  };

  return (
    <NavigationGuardContext.Provider value={{ setGuardActive, guardedPush }}>
      {children}
      <ConfirmLeaveDialog
        open={pendingHref !== null}
        onOpenChange={(open) => {
          if (!open) setPendingHref(null);
        }}
        onConfirm={handleConfirm}
      />
    </NavigationGuardContext.Provider>
  );
}
